import {getToken, refreshCurrentUser} from "./useToken"
import {wrappedGet, wrappedPost} from "./AxiosHelper"
import {Skeleton} from "primereact/skeleton"

export function fetcher(url, data = {}) {
    const {user_id, facility} = getToken()

    return wrappedPost(url, {
        user_id,
        facility,
        ...data,
    }).then((res) => res.data.results)
}

export function fetcherGet(url) {
    return wrappedGet(url).then((res) => res.data.results)
}

export function dynamicSort(keys, descending) {
    return function (a, b) {
        for (const key of keys) {
            let aValue
            let bValue
            if (Array.isArray(key)) {
                aValue = key[1](a)
                bValue = key[1](b)
            } else {
                aValue = a[key]
                bValue = b[key]
            }

            // Always throw Infinity then nulls to end
            const aValueInf = aValue === Infinity
            const bValueInf = bValue === Infinity
            const aValueNull = aValue === null || aValue === undefined
            const bValueNull = bValue === null || bValue === undefined

            // Sorting logic
            if (aValueNull && bValueNull) {
                continue
            }
            if (aValueNull) {
                return 1
            }
            if (bValueNull) {
                return -1
            }

            if (aValueInf && bValueInf) {
                continue
            }
            if (aValueInf) {
                return 1
            }
            if (bValueInf) {
                return -1
            }

            if (aValue === bValue) {
                continue
            }

            if (descending) {
                return aValue > bValue ? -1 : 1
            } else {
                return aValue < bValue ? -1 : 1
            }
        }

        return 0 // If all keys are equal
    }
}

export function miltaryTimeToReadableFormat(time_string) {
    if (["", NaN, null, undefined].includes(time_string)) {
        return ""
    }
    time_string = time_string.toString()
    let hours = time_string.slice(0, 2)
    let am_or_pm = hours > 12 ? "pm" : "am"
    hours = hours > 12 ? hours - 12 : hours
    let minutes = time_string.slice(2, 4)
    return hours + ":" + minutes + am_or_pm
}

export const bulkFilter = (data, filters) => {
    return filters.reduce((filteredData, filter) => {
        let [keys, value, flag] = filter
        // Don't apply filter if no value
        if (typeof value !== "boolean" && (!value || !value.length)) {
            return filteredData
        }

        // If keys is just a string, turn it into an array so the function works the same
        if (!Array.isArray(keys)) {
            keys = [keys]
        }
        return filteredData.filter((item) =>
            keys.some((key) => {
                switch (flag) {
                    case "inexact":
                        return item[key] && item[key].toLowerCase().includes(value.toLowerCase())
                    case "boolean":
                        if (Array.isArray(item[key])) {
                            const arrayNonZero = !!item[key].length > 0
                            return arrayNonZero === (value === "Yes")
                        } else {
                            return typeof item[key] === "boolean"
                                ? item[key] === value
                                : !!item[key] === (value === "Yes")
                        }
                    case "includes":
                        return item[key] && value.includes(item[key])
                    case "array":
                        return item[keys] && value.some((v) => item[keys].includes(v))
                    default:
                        return item[key] && item[key].toLowerCase() === value.toLowerCase()
                }
            })
        )
    }, data)
}

export function getOptionsList(data, keys) {
    const output = []
    for (const key of keys) {
        const values = [...new Set(data.map((obj) => obj[key]))]
        const options = []
        values.forEach((val) => {
            if (!!val) {
                options.push({label: val, value: val})
            }
        })
        output.push(options)
    }
    return output
}

export function buildOrgOptions(orgs) {
    return orgs.map((org) => {
        return {label: org.name, value: org.id}
    })
}

export function buildFacilityOptions(facilities) {
    return facilities.map((facility) => {
        return {label: facility.name, value: facility.id}
    })
}

export function buildUnitOptions(orgId, units) {
    return units
        .filter((unit) => unit.organization_id === orgId)
        .map((unit) => {
            return {label: unit.name, value: unit.id}
        })
}

export function SkeletonRows({colCount, rowCount}) {
    return (
        <>
            {Array.from({length: rowCount}, (_, i) => (
                <tr key={`row-${i}`}>
                    {Array.from({length: colCount}, (_, i) => (
                        <td key={`col-${i}`}>
                            <Skeleton className="w-100 h-40px" />
                        </td>
                    ))}
                </tr>
            ))}
        </>
    )
}

export function getLoadingRows(rowCount) {
    return Array.from({length: rowCount}, (_, i) => ({id: `loading-${i}`, loading: true}))
}

// Expected dateString format is "YYYY-MM-DD" or "YYYY-MM-DDTHH:MM:SSZ".
// Returns a JS Date object or null if no dateString provided.
// If dateString is already a Date object, it is returned as is.
// Note that the month in the JS Date constructor is 0-indexed, so we subtract 1 from the month value.
export function getJSDate(dateString) {
    if (!dateString) return null
    if (dateString instanceof Date) return dateString
    // Check if it includes a time separator 'T' or a timezone offset indicator
    if (dateString.includes("T") || dateString.includes("Z")) {
        const parsedDate = new Date(dateString)
        // Ensure the string was valid ISO before returning
        return isNaN(parsedDate.getTime()) ? null : parsedDate
    }
    const [year, month, day] = dateString.split("-").map(Number)
    return new Date(year, month - 1, day)
}

export function getLocaleDate(dateString) {
    const jsDate = getJSDate(dateString)
    if (jsDate instanceof Date) return jsDate.toLocaleDateString()
    return dateString
}

export const formatDateTime = (isoString) => {
    if (!isoString) return ""

    const date = new Date(isoString)

    const datePart = date.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
    })

    const timePart = date
        .toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        })
        .toLowerCase()

    return `${datePart} ${timePart}`
}

export function formatPercent(value) {
    if (value == null || Number.isNaN(Number(value))) return "0%"

    const numericValue = Number(value)
    if (numericValue <= 0) return "0%"
    if (numericValue < 1) return "0%"

    const percentage = Math.min(numericValue, 100)
    return `${Math.trunc(percentage)}%`
}

export function scoreBadge(score, max) {
    const percent = (score / max) * 100
    const badge = {
        badge: "",
        background: "bg-white",
        border: "border-gray-400",
        color: "",
        score: score,
        textColor: "",
        width: percent,
        order: 99,
        // badgeColor is used since text-color is used for the orienation
        // badges and not for the badges that are only color circles
        badgeColor: "",
    }
    if (percent > 0 && percent <= 40) {
        badge.badge = "Lots of Learning Left"
        badge.background = "badge-light-danger"
        badge.border = "border-danger"
        badge.color = "bg-danger"
        badge.textColor = "text-black"
        badge.badgeColor = "text-light-danger"
        badge.order = 1
    } else if (percent > 40 && percent <= 60) {
        badge.badge = "Making Progress"
        badge.background = "badge-light-warning"
        badge.border = "border-warning"
        badge.color = "bg-warning"
        badge.textColor = "text-black"
        badge.badgeColor = "text-light-warning"
        badge.order = 2
    } else if (percent > 60 && percent <= 80) {
        badge.badge = "Almost Done"
        badge.background = "badge-light-success"
        badge.border = "border-success"
        badge.color = "bg-primary"
        badge.textColor = "text-black"
        badge.badgeColor = "text-light-success"
        badge.order = 3
    } else if (percent > 80) {
        badge.badge = "Ready for Practice"
        badge.background = "badge-success"
        badge.border = "border-success"
        badge.color = "bg-success"
        badge.textColor = "text-white"
        badge.badgeColor = "text-success"
        badge.order = 4
    }
    return badge
}

export const calculateMetricsRoutes = (arrayOfLinks, role) => {
    const newRoutes = arrayOfLinks
        .map((route) => {
            if (route.skip) {
                return null
            }
            if (
                (route.roleRequired && route.roleRequired.includes(role)) ||
                (route.roleExcluded && !route.roleExcluded.includes(role))
            ) {
                return route
            } else {
                return null
            }
        })
        .filter((item) => item !== null)
    return newRoutes
}

export const QUESTION_TYPE = {
    RANGE: "range",
    NUMBER: "number",
    TEXT: "text",
    DATE: "date",
    SELECT: "select",
    BOOLEAN: "boolean",
    MULTI_SELECT_WITH_TEXT: "multiSelectWithText",
    RANGE_WITH_NOT_APPLICABLE: "rangeWithNotApplicable",
    SELECT_FROM_API: "selectFromApi",
    TOPIC_INTRO: "topicIntro",
    SELECT_PRECEPTOR: "selectPreceptor",
    CASEYFINK: "caseyFink",
    CASEYFINKNONA: "caseyFinkNoNa",
}

export const cancelPersona = async () => {
    await wrappedPost("auth/cancel-persona")

    // Reload current user into sessionStorage
    await refreshCurrentUser()

    window.location.href = "/home"
}

// putting this here until we can bootstrap the constants from the backend
export const QUESTION_TYPE_IDS = {
    TOPIC_INTRO: 10,
}

export const normalizeDate = (date) => {
    if (!date) return null

    return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

export const formatDateForApi = (date) => {
    if (!date) return null

    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
}

export const ORIENTATION_STATUS = {
    ONBOARDING: 1,
    ACTIVE: 2,
    PAUSED: 3,
    ATTRITIONED: 4,
    CANCELLED: 5,
    COMPLETED: 6,
    TRANSFER: 7,
    INELIGIBLE: 8,
}

export const formatDate = (value) => (value ? new Date(value).toLocaleDateString("en-US") : "—")

export const getInitials = (name = "") => {
    return name
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map((part) => part[0]?.toUpperCase())
        .slice(0, 2)
        .join("")
}

export const capitalizePersonName = (name = "") => {
    return name
    .trim()
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
} 

export const calcEndDate = (startDate, baseWeeks) => {
    if (!startDate || baseWeeks === null || baseWeeks === undefined) {
        return ""
    }

    const endDate = getJSDate(startDate)

    if (!(endDate instanceof Date) || Number.isNaN(endDate.getTime())) {
        return ""
    }

    endDate.setDate(endDate.getDate() + Number(baseWeeks) * 7)

    return getLocaleDate(endDate)
}

export const getShiftedWeeks = (orientation) => {
    let educator_shifted_weeks = null
    if (orientation.educator_shifted_weeks && orientation.educator_shifted_weeks > 0) {
        educator_shifted_weeks = `(+${orientation.educator_shifted_weeks})`
    } else if (orientation.educator_shifted_weeks && orientation.educator_shifted_weeks < 0) {
        educator_shifted_weeks = `(-${orientation.educator_shifted_weeks})`
    } 

    return educator_shifted_weeks
}

export const isJSON = (data) => {
    try {
        JSON.parse(data);
        return true;
    } catch (e) {
        return false;
    }
}
